import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType } from '@/api/login/types'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { loginOutApi } from '@/api/login'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import axios from 'axios'

interface UserState {
  userInfo?: UserType
  tokenKey: string
  token: string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
  businesses: any[]
  intervalId?: any
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: '',
      roleRouters: undefined,
      // 记住我
      rememberMe: true,
      loginInfo: undefined,
      businesses: [],
      intervalId: null
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.token
    },
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    getBusinesses(): any[] {
      return this.businesses
    },
    shouldSendHeartbeat(): boolean {
      return (
        this.userInfo?.role.indexOf('order_business') !== -1 ||
        this.userInfo?.role.indexOf('cost_business') !== -1
      )
    },
    isItBusinessMan(): boolean {
      return [1,2,6].includes(this.userInfo?.userType ?? 0)
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
    },
    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    setBusiness(businessList: any[]) {
      this.businesses = businessList
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          const res = await loginOutApi().catch(() => {})
          if (res) {
            this.reset()
          }
        })
        .catch(() => {})
    },
    startHeartbeat() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
      const sendHeartbeat = async () => {
        try {
          const response = await axios.get('/api/admin/heartbeat', { withCredentials: true })
          console.log('Heartbeat:', response.data)
        } catch (error) {
          console.error('Error sending heartbeat:', error)
        }
      }
      if (
        this.userInfo?.role.indexOf('order_business') !== -1 ||
        this.userInfo?.role.indexOf('cost_business') !== -1
      ) {
        const intervalId = setInterval(sendHeartbeat, 1000 * 60 * 15)
        this.intervalId = intervalId
      }
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setUserInfo(undefined)
      this.setRoleRouters([])
      this.setBusiness([])
      console.log(this.intervalId)
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
      sessionStorage.clear()
      router.replace('/login')
    },
    logout() {
      this.reset()
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    someRole(...roles: string[]){
      return roles.includes(this.userInfo?.role ?? '')
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
